import * as Sentry from "@sentry/browser"
import { Integration } from "@sentry/types/types/integration"
const appConfiguration = require("./config/sentry.json")
const sentryDns = appConfiguration.integrations?.sentry?.dsn
const sentryEnabled = appConfiguration.settings.enableSentryIntegration
const sentryEnvironment = appConfiguration.settings?.sentryOptionEnv
const sentryReplaySessionEnabled =
  appConfiguration.settings.sentrySessionReplayEnable
const sentryTracesSampleEnable =
  appConfiguration.settings.sentryTracesSampleEnable
const sentryTracesSampleRate = appConfiguration.settings.sentryTracesSampleRate
const sentryTracePropagationTargets =
  appConfiguration.settings.sentryTracePropagationTargets
const sentryCaptureConsoleEnable =
  appConfiguration.settings.sentryCaptureConsoleEnable
const sentryHttpClientEnable = appConfiguration.settings.sentryHttpClientEnable

const integrations: Integration[] = [
  Sentry.browserTracingIntegration({
    enableInp: true,
  }),
]

// Only load Session Replay when its sample rates are configured below,
// otherwise the SDK warns: "Replay is disabled because neither
// `replaysSessionSampleRate` nor `replaysOnErrorSampleRate` are set."
if (sentryReplaySessionEnabled) {
  integrations.push(Sentry.replayIntegration())
}

if (sentryCaptureConsoleEnable) {
  integrations.push(
    Sentry.captureConsoleIntegration({
      levels: ["log", "info", "warn", "error"],
    })
  )
}

if (sentryHttpClientEnable) {
  integrations.push(Sentry.httpClientIntegration())
}

const sentrySendErrorsToGoogleAnalytic =
  appConfiguration.settings.sentrySendErrorsToGoogleAnalytic

declare global {
  interface Window {
    dataLayer: any[]
    dataLayer2: any[]
  }
}

// default options
const options: Sentry.BrowserOptions = {
  dsn: sentryDns,
  environment: sentryEnvironment,
  release: `${process.env.GATSBY_APP_VERSION}`,
  integrations,
  tracesSampleRate: 0,
  tracePropagationTargets: [],
  // Drop dropped/aborted-connection errors at the SDK level, before
  // `beforeSend` runs. These almost always indicate a tab closed mid
  // request, an ad-blocker, a captive portal, a flaky mobile network or
  // a link-scanner abort, and are not actionable from our side.
  // Matched against `event.message` and every `event.exception.values[*].value`.
  ignoreErrors: [
    "Failed to fetch", // Chrome / Firefox
    "Load failed", // Safari
    "NetworkError when attempting to fetch resource", // Firefox alt
    "The network connection was lost", // Safari / iOS
    "The request timed out", // Safari / iOS
    "cancelled", // Safari abort
    /^Network Error$/, // Axios
    "AbortError", // Explicit abort
    "TypeError: NetworkError",
    "The above error occurred in task xr",
    "We couldn't load",
    // react-map-gl v6 pinch-zoom: Hammer.js can emit scale=0 on degenerate
    // touch geometry, tripping an over-strict assertion in MapState.
    // Fixed in v7 (no Hammer). Drop until we upgrade.
    "`scale` must be a positive number",
  ],
  beforeSend(event, hint) {
    // Drop events coming from crawlers and email link-scanning services.
    // These bots prefetch URLs from emails and frequently abort the request,
    // which generates noise in Sentry instead of representing real user issues.
    const userAgent = navigator.userAgent.toLowerCase()
    const blockedUserAgents = [
      // Generic crawlers
      "bytespider",
      "bytedance",
      // Email security / URL rewriting services
      "mimecast",
      "proofpoint",
      "urldefense", // Proofpoint URL Defense
      "barracuda",
      "symantec",
      "clicktime", // Symantec Click-time URL Protection
      "messagelabs",
      "trendmicro",
      "sophos",
      "fortinet",
      "darktrace",
      "officevalidatelinks", // Microsoft 365 link validation
      "safelinks", // Microsoft Defender Safe Links
    ]
    const isBlockedUserAgent = blockedUserAgents.some(blockUserAgent =>
      userAgent.includes(blockUserAgent)
    )
    if (isBlockedUserAgent) {
      return null
    }

    // Drop any error captured while the document is not visible.
    // Email scanners and link prefetchers load pages in a hidden /
    // background context and never bring them to the foreground, so any
    // error happening in that state is almost certainly not a real user
    // experiencing the issue.
    const isHidden =
      typeof document !== "undefined" && document.visibilityState !== "visible"
    if (isHidden) {
      return null
    }

    try {
      const value = event?.exception?.values?.[0]
      const type = value?.type || "Error"
      const message =
        event?.message ||
        value?.value ||
        (hint && hint.originalException && String(hint.originalException)) ||
        "Unknown error"

      const analyticEvent = {
        event: "exception",
        description: `${type}: ${message}`.slice(0, 150),
        fatal: event.level === "fatal" || false,
        sentry_event_id: event.event_id,
        sentry_level: event.level || "error",
        sentry_release: event.release,
        sentry_env: event.environment,
      }

      if (window && sentrySendErrorsToGoogleAnalytic) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(analyticEvent)

        window.dataLayer2 = window.dataLayer2 || []
        window.dataLayer2.push(analyticEvent)
      }
    } catch (e) {
      // do nothing
    }

    return event
  },
}

// trace sample options
if (sentryTracesSampleEnable) {
  options.tracesSampleRate = sentryTracesSampleRate / 100
  options.tracePropagationTargets = [
    "localhost",
    ...sentryTracePropagationTargets,
  ]
}

// session replay options
if (sentryReplaySessionEnabled) {
  options.replaysSessionSampleRate =
    appConfiguration.settings.sentrySessionReplaySampleRate / 100
  options.replaysOnErrorSampleRate =
    appConfiguration.settings.sentrySessionReplayOnErrorSampleRate / 100
}

if (sentryEnabled) {
  Sentry.init(options)
}
